/**
 * UltimateArc — compact combo meter sitting under the TIME chip.
 *
 * NBA Jam "He's on fire!" vibe. Starts as a clean gold bar that fills
 * from the center outward. As the combo climbs, a flickering flame
 * layer ignites on top — orange → red — with animated tongue-shaped
 * particles. Full charge = screen-wide celebratory pulse.
 *
 * Width is capped at COMBO_BAR_W so the bar stays visually contained
 * below the center TIME chip, not sprawling across the whole top edge.
 *
 * Usage (once per scene):
 *   const arc = new UltimateArc(scene);
 *   arc.setProgress(0.35);   // 35% charged
 *   arc.flashFull();          // trigger celebration when full
 *   arc.destroy();
 */

import Phaser from 'phaser';
import { GAME_CONFIG } from '@/config/constants';

export class UltimateArc {
  private static readonly COMBO_BAR_W = 340;   // matches roughly the width of a fat HUD chip
  private static readonly COMBO_BAR_H = 10;
  private static readonly COMBO_BAR_Y = 124;   // under the TIME chip (chip bottom ~108)
  private static readonly FIRE_THRESHOLD = 0.45; // flame layer kicks in at ~45% charge

  private scene: Phaser.Scene;
  private barGfx: Phaser.GameObjects.Graphics;
  private flameGfx: Phaser.GameObjects.Graphics;
  private frameGfx: Phaser.GameObjects.Graphics;
  private progress = 0;
  private pulseTween?: Phaser.Tweens.Tween;
  private fadeTween?: Phaser.Tweens.Tween;
  private flameTime = 0;
  private flameTicker?: Phaser.Time.TimerEvent;
  private emberEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private emberTexKey = 'ultarc_ember';

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Frame (dark track underneath — reads the bar against the navy HUD wash).
    // Start hidden — the whole arc fades in as combo begins so there's no
    // empty black trough sitting in the HUD between combos.
    this.frameGfx = scene.add.graphics().setDepth(95).setScrollFactor(0).setAlpha(0);
    this.barGfx = scene.add.graphics().setDepth(96).setScrollFactor(0).setAlpha(0);
    this.flameGfx = scene.add.graphics().setDepth(97).setScrollFactor(0).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);

    this.ensureEmberTexture();

    // Animated flame flicker — re-renders the flame layer ~20fps so it
    // shimmers without being so fast it reads as noise. Cheap: just a
    // graphics redraw, no new GameObjects per tick.
    this.flameTicker = scene.time.addEvent({
      delay: 50,
      loop: true,
      callback: () => {
        this.flameTime += 0.05;
        if (this.progress >= UltimateArc.FIRE_THRESHOLD) this.redrawFlames();
      },
    });

    this.redraw();
  }

  /** 0..1 — fraction of ult charged. Clamped internally. */
  setProgress(p: number): void {
    const clamped = Math.max(0, Math.min(1, p));
    if (Math.abs(clamped - this.progress) < 0.005) return;
    const wasBelowFire = this.progress < UltimateArc.FIRE_THRESHOLD;
    const nowAtOrAboveFire = clamped >= UltimateArc.FIRE_THRESHOLD;
    const wasZero = this.progress <= 0;
    const isZero = clamped <= 0;
    this.progress = clamped;
    this.redraw();

    // Fade the whole arc in/out so we never leave an empty black trough
    // sitting in the HUD between combos.
    if (wasZero && !isZero) this.fadeArc(1, 180);
    else if (!wasZero && isZero) this.fadeArc(0, 320);

    // Ignite embers the moment the bar crosses into the fire zone
    if (wasBelowFire && nowAtOrAboveFire) this.igniteEmbers();
    if (!nowAtOrAboveFire) this.stopEmbers();
  }

  private fadeArc(targetAlpha: number, duration: number): void {
    this.fadeTween?.stop();
    this.fadeTween = this.scene.tweens.add({
      targets: [this.frameGfx, this.barGfx, this.flameGfx],
      alpha: targetAlpha,
      duration,
      ease: targetAlpha > 0 ? 'Quad.easeOut' : 'Quad.easeIn',
    });
  }

  /** Pulsing alpha flash — call when ult is ready or fires. */
  flashFull(): void {
    this.pulseTween?.stop();
    const obj = { a: 1 };
    this.pulseTween = this.scene.tweens.add({
      targets: obj,
      a: 0.35,
      duration: 220,
      yoyo: true,
      repeat: 4,
      onUpdate: () => {
        this.barGfx?.setAlpha(obj.a);
        this.flameGfx?.setAlpha(obj.a);
      },
      onComplete: () => {
        this.barGfx?.setAlpha(1);
        this.flameGfx?.setAlpha(1);
      },
    });
  }

  destroy(): void {
    this.pulseTween?.stop();
    this.fadeTween?.stop();
    this.flameTicker?.remove();
    this.flameTicker = undefined;
    this.emberEmitter?.stop();
    this.emberEmitter?.destroy();
    this.emberEmitter = undefined;
    this.barGfx?.destroy();
    this.flameGfx?.destroy();
    this.frameGfx?.destroy();
  }

  // ─── Internals ─────────────────────────────────────────────────────────────

  private redraw(): void {
    if (!this.barGfx || !this.barGfx.scene) return;

    const { WIDTH } = GAME_CONFIG;
    const W = UltimateArc.COMBO_BAR_W;
    const H = UltimateArc.COMBO_BAR_H;
    const Y = UltimateArc.COMBO_BAR_Y;
    const cx = WIDTH / 2;
    const left = cx - W / 2;
    const r = H / 2;

    // ─── FRAME ───────────────────────────────────────────────────────────
    this.frameGfx.clear();
    // Shadow trough
    this.frameGfx.fillStyle(0x000000, 0.45);
    this.frameGfx.fillRoundedRect(left - 1, Y - 1, W + 2, H + 2, r + 1);
    // Track (dark navy)
    this.frameGfx.fillStyle(0x0a1a2e, 0.85);
    this.frameGfx.fillRoundedRect(left, Y, W, H, r);
    // Thin bronze hairline on top
    this.frameGfx.lineStyle(1, 0xb27a3a, 0.6);
    this.frameGfx.strokeRoundedRect(left, Y, W, H, r);

    // ─── BAR FILL ────────────────────────────────────────────────────────
    this.barGfx.clear();
    if (this.progress <= 0) {
      this.flameGfx.clear();
      return;
    }

    const filled = W * this.progress;
    const halfFill = filled / 2;
    const fx = cx - halfFill;
    const fw = filled;

    // Color lerps: gold → amber → red as it fills
    const baseColor = this.lerpColor(
      this.progress < 0.5 ? [0xffd166, 0xffb347, this.progress * 2]
        : [0xffb347, 0xff6b3d, (this.progress - 0.5) * 2]
    );

    // Gradient-ish fill: layer a slightly brighter top half for shine
    this.barGfx.fillStyle(baseColor, 0.95);
    this.barGfx.fillRoundedRect(fx, Y, fw, H, r);

    // Top highlight hairline
    this.barGfx.fillStyle(0xffffff, 0.35);
    this.barGfx.fillRoundedRect(fx + 2, Y + 1, Math.max(0, fw - 4), 2, 1);

    // Glow halo when filling
    this.barGfx.fillStyle(baseColor, 0.2);
    this.barGfx.fillRoundedRect(fx - 4, Y - 3, fw + 8, H + 6, r + 3);

    // Fire layer on top once past threshold
    if (this.progress >= UltimateArc.FIRE_THRESHOLD) {
      this.redrawFlames();
    } else {
      this.flameGfx.clear();
    }
  }

  /**
   * Draw animated flame tongues rising out of the top of the bar fill.
   * Re-rendered every 50ms by the ticker. Uses cheap sine-based tongue
   * shapes (no particle for the core flame — particles are reserved for
   * the spark embers above the bar).
   */
  private redrawFlames(): void {
    if (!this.flameGfx || !this.flameGfx.scene) return;
    const { WIDTH } = GAME_CONFIG;
    const W = UltimateArc.COMBO_BAR_W;
    const H = UltimateArc.COMBO_BAR_H;
    const Y = UltimateArc.COMBO_BAR_Y;
    const cx = WIDTH / 2;
    const filled = W * this.progress;
    const fx = cx - filled / 2;

    this.flameGfx.clear();

    // Intensity ramps from threshold → 1
    const t = (this.progress - UltimateArc.FIRE_THRESHOLD) / (1 - UltimateArc.FIRE_THRESHOLD);
    const intensity = Math.max(0.35, Math.min(1, t));

    // Flame color: orange → red-white as it fully charges
    const flameCore = this.progress >= 0.95 ? 0xfff0a0
      : this.progress >= 0.75 ? 0xffd84d
      : 0xff9a3c;
    const flameRim = this.progress >= 0.95 ? 0xff8844 : 0xff4422;

    // 5–9 flame tongues depending on bar width
    const tongueCount = Math.max(5, Math.round(filled / 32));
    for (let i = 0; i < tongueCount; i++) {
      const u = i / (tongueCount - 1 || 1);
      const tx = fx + 8 + u * (filled - 16);
      // Wobble — each tongue has its own phase so they flicker independently
      const phase = i * 0.73 + this.flameTime * (2 + (i % 3) * 0.4);
      const tongueH = (10 + intensity * 14) * (0.8 + Math.sin(phase) * 0.35);
      const tongueW = 6 + Math.sin(phase * 1.3) * 1.5;

      // Outer rim (red)
      this.flameGfx.fillStyle(flameRim, 0.55 * intensity);
      this.flameGfx.fillEllipse(tx + Math.sin(phase * 0.7) * 2, Y - tongueH * 0.35, tongueW + 2, tongueH + 3);

      // Core (bright orange/yellow)
      this.flameGfx.fillStyle(flameCore, 0.85 * intensity);
      this.flameGfx.fillEllipse(tx + Math.sin(phase * 0.7) * 2, Y - tongueH * 0.45, tongueW, tongueH);
    }

    // Subtle underglow washing the fire tint into the bar itself
    this.flameGfx.fillStyle(flameRim, 0.18 * intensity);
    this.flameGfx.fillRoundedRect(fx - 2, Y - 2, filled + 4, H + 4, H / 2 + 2);
  }

  /** Generate a tiny soft-dot texture used for the ember particle burst. */
  private ensureEmberTexture(): void {
    if (this.scene.textures.exists(this.emberTexKey)) return;
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    // Soft radial ember — bright core, orange halo
    g.fillStyle(0xffffff, 1);
    g.fillCircle(8, 8, 3);
    g.fillStyle(0xffb347, 0.6);
    g.fillCircle(8, 8, 7);
    g.generateTexture(this.emberTexKey, 16, 16);
    g.destroy();
  }

  /** Start the rising-ember particles above the bar. */
  private igniteEmbers(): void {
    if (this.emberEmitter || !this.scene) return;
    const { WIDTH } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const W = UltimateArc.COMBO_BAR_W;
    const Y = UltimateArc.COMBO_BAR_Y;

    this.emberEmitter = this.scene.add.particles(cx, Y - 2, this.emberTexKey, {
      x: { min: -W / 2 + 10, max: W / 2 - 10 },
      y: 0,
      quantity: 1,
      frequency: 80,
      lifespan: { min: 520, max: 900 },
      speedY: { min: -90, max: -160 },
      speedX: { min: -22, max: 22 },
      scale: { start: 0.75, end: 0.1 },
      alpha: { start: 0.95, end: 0 },
      tint: [0xffd84d, 0xff8844, 0xff4422, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
    })
      .setDepth(98)
      .setScrollFactor(0);
  }

  private stopEmbers(): void {
    if (!this.emberEmitter) return;
    this.emberEmitter.stop();
    this.emberEmitter.destroy();
    this.emberEmitter = undefined;
  }

  /**
   * Linear interpolate two hex colors.
   * Input form: [colorA, colorB, t] packed in a single array so callers
   * can pass a computed (a,b,t) tuple inline.
   */
  private lerpColor([a, b, t]: [number, number, number]): number {
    const clamp = Math.max(0, Math.min(1, t));
    const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
    const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
    const rr = Math.round(ar + (br - ar) * clamp);
    const rg = Math.round(ag + (bg - ag) * clamp);
    const rb = Math.round(ab + (bb - ab) * clamp);
    return (rr << 16) | (rg << 8) | rb;
  }
}
